import { Grid, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IChecklistItemPopulated, IChecklistItemRow, onChecklistRowChange } from "@mctypes/checklist.types";
import { IMinUserDepricated, Paths, PathValue } from "@mctypes/global.types";
import { IconCalendarWeek } from "@tabler/icons-react";
import React from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation,
} from "../../../../../openapi";
import { McTitle } from "../../../../components";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { McMantineUserSelect } from "../../../../components/McMantineUserSelect/McMantineUserSelect";
import { useAppSelector } from "../../../../hooks";
import ChecklistReportImagesEditor from "../ChecklistReportImagesEditor";
import "../checklistBoardApi.enhance";
import {
  selectChecklistModalCheckColumns,
  selectChecklistModalUserComboboxData,
  selectChecklistModalViewModel,
} from "../checklistSelectors";
import StampCheck from "../StampCheck";

const getUserId = (user?: string | IMinUserDepricated | null) => {
  if (user == null) return null;
  if (typeof user === "string") return user;
  return user._id ?? null;
};

const getUserName = (user?: string | IMinUserDepricated | null) => {
  if (user == null) return null;
  if (typeof user === "string") return user;
  return user.name;
};

const ChecklistDetailsRowChecksSectionRefactored: React.FC = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const location = useLocation();
  const checklistRowId = new URLSearchParams(location.search).get("id");
  const { checklist, checklistRow, editable, isItem, isTemplate, projectId } = useAppSelector((state) =>
    selectChecklistModalViewModel(state, checklistId, checklistRowId),
  );
  const checkColumns = useAppSelector(selectChecklistModalCheckColumns);
  const usersComboboxData = useAppSelector(selectChecklistModalUserComboboxData);
  const [updateCategory] = usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation();
  const [updateItem] = usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation();

  if (checklist == null || checklistRow == null) {
    return null;
  }

  const updateItemAttribute = <P extends Paths<IChecklistItemPopulated, 4>>(
    rowId: string,
    path: P,
    oldValue: string,
    newValue: PathValue<IChecklistItemPopulated, P>,
  ) =>
    void updateItem({
      projectId,
      checklistId: checklist._id,
      itemId: rowId,
      body: {
        path,
        oldValue,
        newValue,
      },
    });

  const updateCategoryAttribute = <T extends keyof IChecklistItemRow>(
    attribute: string,
    newValue: IChecklistItemRow[T],
    oldValue: string,
  ) =>
    void updateCategory({
      projectId,
      checklistId: checklist._id,
      categoryId: checklistRow._id,
      body: {
        attribute,
        oldValue,
        newValue,
      },
    });

  const updateRowParameter: onChecklistRowChange = (oldValue, newValue, parameter) => {
    if (oldValue === newValue) {
      return;
    }

    if (isItem === true) {
      updateItemAttribute(
        checklistRow._id,
        parameter as Paths<IChecklistItemPopulated, 4>,
        oldValue?.toString() ?? "",
        newValue as PathValue<IChecklistItemPopulated, Paths<IChecklistItemPopulated, 4>>,
      );
      return;
    }

    updateCategoryAttribute(parameter, newValue, oldValue?.toString() ?? "");
  };

  return (
    <Stack gap="sm">
      {isItem === true ? (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <McTitle title="Nummer">
                <Text size="sm">{checklistRow.itemId}</Text>
              </McTitle>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 8 }}>
              <McTitle title="Kategori">
                <Text size="sm">{`${checklistRow.categoryId}: ${checklistRow.originalCategory.title}`}</Text>
              </McTitle>
            </Grid.Col>
          </Grid>
          {isTemplate === false && (
            <Stack gap="xs">
              {checkColumns.map((checkType, index) => (
                <div key={index}>
                  {checkType != null && checklistRow.originalItem?.checks[checkType.prop]?.status !== "DISABLED" && (
                    <McTitle title={checkType.name}>
                      <StampCheck
                        disabled={checklistRow.inactive.isChecked === true}
                        toggle={(rowId, oldValue, newValue) =>
                          updateItemAttribute(rowId, `checks.${checkType.prop}.status`, oldValue, newValue)
                        }
                        checkItem={checklistRow.originalItem?.checks[checkType.prop]}
                        row={checklistRow}
                        stampSettings={checklist.stampSettings}
                      />
                    </McTitle>
                  )}
                </div>
              ))}
            </Stack>
          )}
        </>
      ) : (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
              <McTitle title="Kategorinummer">
                {editable === true ? (
                  <McInputNew
                    required={true}
                    value={checklistRow.categoryId}
                    pattern="^\d+$"
                    onBlur={(event) =>
                      updateRowParameter(checklistRow.categoryId, Number(event.target.value), "categoryId")
                    }
                  />
                ) : (
                  <Text size="sm">{checklistRow.categoryId}</Text>
                )}
              </McTitle>
            </Grid.Col>

            {isTemplate === false && (
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <McTitle title="Frist">
                  <DateInput
                    styles={{ input: { minHeight: "2.625rem", height: "2.625rem" } }}
                    clearable
                    valueFormat="DD.MM.YYYY"
                    value={
                      checklistRow.originalCategory.dueDate != null
                        ? new Date(checklistRow.originalCategory.dueDate)
                        : null
                    }
                    onChange={(value) =>
                      updateRowParameter(
                        checklistRow.dueDate,
                        value != null ? (value as unknown as Date).toISOString() : undefined,
                        "dueDate",
                      )
                    }
                    rightSection={
                      checklistRow.originalCategory.dueDate == null && (
                        <IconCalendarWeek color="var(--mantine-color-warmgray-3)" />
                      )
                    }
                  />
                </McTitle>
              </Grid.Col>
            )}
          </Grid>

          {isTemplate === false && (
            <Grid>
              {checkColumns.map((checkType, index) => (
                <Grid.Col span={{ base: 12, md: 4 }} key={index}>
                  {checkType != null && (
                    <McTitle title={`Ansvarlig ${checkType.name}`}>
                      <McMantineUserSelect
                        data={usersComboboxData}
                        value={getUserId(checklistRow.originalCategory?.checks?.[checkType.prop]?.assignedTo)}
                        onChange={(value) =>
                          updateCategoryAttribute(
                            `checks.${checkType.prop}.assignedTo`,
                            value,
                            getUserName(checklistRow.originalCategory?.checks?.[checkType.prop]?.assignedTo) ?? "",
                          )
                        }
                      />
                    </McTitle>
                  )}
                </Grid.Col>
              ))}
            </Grid>
          )}
        </>
      )}

      {isTemplate === false && isItem === true && (
        <>
          <McTitle title="Rapportkommentar">
            <McTextAreaNew
              onBlur={(event) => updateRowParameter(checklistRow.comment, event.target.value, "comment")}
              value={checklistRow.comment}
              autosize
            />
          </McTitle>
          <McTitle title="Bilde">
            <ChecklistReportImagesEditor
              images={(checklistRow.commentImages ?? []).slice(0, 1)}
              onSave={(oldValue, newValue) => updateRowParameter(oldValue, newValue, "commentImages")}
            />
          </McTitle>
        </>
      )}
    </Stack>
  );
};

export default ChecklistDetailsRowChecksSectionRefactored;
